// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Logo overlay rendered as an SVG badge.
//
// A screen-fixed text wordmark in an overlay <g>, anchored to the
// bottom-left corner of the current viewBox and gated by the `logo` prop.
//
// Split, like the scalebar, into a pure metrics function (tested) and an
// element builder (DOM glue): logoMetrics() decides placement; renderLogo()
// turns it into an SVG node.

import { el } from "./dom.js";

// Product wordmark shown in the corner badge.
const WORDMARK = "TheiaPhyloViewer";

// Badge box in natural (unscaled) pixels. Width is sized to fit the wordmark;
// renderLogo fits the text to this box with textLength so it stays crisp at any
// zoom without shipping a raster asset.
const LOGO_WIDTH = 120;
const LOGO_HEIGHT = 16;
const LOGO_PADDING = 8;

/**
 * Compute the logo badge placement, or null when the logo is disabled or the
 * inputs are unusable. Position is in viewBox units and divided by `scale` so
 * the badge keeps a constant on-screen pixel size as the view zooms.
 *
 * @param {object} props - Viewer props; `logo` gates rendering, optional
 *   `logoPadding` overrides the corner inset (default 8px).
 * @param {{x:number,y:number,w:number,h:number}} viewBox
 * @param {number} scale - Pixels per viewBox unit (svg.clientWidth / viewBox.w).
 * @returns {{x:number, y:number, width:number, height:number}|null}
 */
export function logoMetrics(props, viewBox, scale) {
  if (!props.logo) return null;
  if (!viewBox || !(scale > 0)) return null;

  const margin = (props.logoPadding != null ? props.logoPadding : LOGO_PADDING) / scale;
  const width = LOGO_WIDTH / scale;
  const height = LOGO_HEIGHT / scale;

  return {
    x: viewBox.x + margin,
    y: viewBox.y + viewBox.h - height - margin,
    width,
    height,
  };
}

/**
 * Build the logo overlay <g>, or null when logoMetrics declines to place it.
 *
 * @param {object} props
 * @param {{x:number,y:number,w:number,h:number}} viewBox
 * @param {number} scale
 * @returns {SVGGElement|null}
 */
export function renderLogo(props, viewBox, scale) {
  const m = logoMetrics(props, viewBox, scale);
  if (!m) return null;

  return el("g", { class: "logo", "aria-label": WORDMARK }, [
    el(
      "text",
      {
        x: m.x,
        y: m.y + m.height * 0.8,
        textLength: m.width,
        lengthAdjust: "spacingAndGlyphs",
        "font-family": "sans-serif",
        "font-size": m.height * 0.85,
        "font-weight": "600",
        fill: "#888",
        style: "pointer-events:none;user-select:none;",
      },
      [WORDMARK],
    ),
  ]);
}
